import React, { useEffect, useState, useCallback } from 'react';
import styles from './style.module.scss';
import SearchInput from '../../components/SearchInput/SearchInput';
import ButtonIcon from '../../components/ButtonIcon/ButtonIcon';
import PlusIcon from '../../assets/svg/icons/common/Plus';
import Pagination from '../../components/Pagination/Pagination';
import Table from './components/table';
import { getBanks } from '../../../api';
import useProcessorStore from '../../stores/processor';
import AddProcessor from './components/add';
import { toast, Toaster, ToastBar } from "react-hot-toast";
import BulkAction from './components/bulk-action-bar';

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const ProcessorPage = () => {

  const bankList = useProcessorStore((state) => state.banks);
  const setBankList = useProcessorStore((state) => state.setBanks);
  const addModal = useProcessorStore((state) => state.addModal);
  const setAddModal = useProcessorStore((state) => state.setAddModal);
  const isEdit = useProcessorStore(state => state.isEdit);
  const searchQuery = useProcessorStore(state => state.searchQuery);
  const selectedBanks = useProcessorStore(state => state.selectedBanks);
  const setActiveBank = useProcessorStore(state => state.setActiveBank);
  const setIsEdit = useProcessorStore(state => state.setIsEdit);
  const setSearchQuery = useProcessorStore(state => state.setSearchQuery);
  const page = useProcessorStore(state => state.page);
  const setPage = useProcessorStore(state => state.setPage);
  const itemsPerPage = useProcessorStore(state => state.itemsPerPage);
  const setItemsPerPage = useProcessorStore(state => state.setItemsPerPage);
  const totalItems = useProcessorStore(state => state.totalItems);
  const setTotalItems = useProcessorStore(state => state.setTotalItems);
  const setIsLoading = useProcessorStore(state => state.setIsLoading);
  // Local state for search input
  const [searchInput, setSearchInput] = useState(searchQuery?.search || '');
  
  // Debounced search value
  const debouncedSearch = useDebounce(searchInput, 500);

  const fetchData = async () => {
    setIsLoading(true);
    const bankListResponse = await getBanks({...searchQuery, page: page, per_page: itemsPerPage});
    if (bankListResponse?.status) {
      setBankList(bankListResponse?.data?.data);
      setTotalItems(bankListResponse?.data?.total);
      setItemsPerPage(bankListResponse?.data?.per_page);
    }
    setIsLoading(false);
  }

  // Update search query when debounced value changes
  useEffect(() => {
    setSearchQuery({ ...searchQuery, search: debouncedSearch });
  }, [debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [searchQuery, page]);

  return (
    <div className={styles.pageContainer} >
      <div className={styles.contentSection} >
      <div className={styles.header}>
        <div className={styles.headerRow}>
          <h1 className={styles.heading}>Processors</h1>
          <div className={styles.headerAction}>
            <ButtonIcon
              bgColor={'#4C70EF'}
              textColor='#FFF'
              onClick={() => setAddModal(true)}
            >
              <PlusIcon />
              <span>Add Processor</span>
            </ButtonIcon>
          </div>
        </div>
        <div className={styles.searchInputContainer}>
          <SearchInput
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
            }}
          />
        </div>
      </div>

      <div className={styles.card} >
        <Table
          data={bankList}
          fetchData={fetchData}
        />

        {totalItems > 0 && (
          <div className={styles.paginationContainer} >
            <Pagination
              pageCount={Math.ceil(totalItems / itemsPerPage)}
            currentPage={page -1}
            onPageChange={(page) => {
              setPage(page.selected + 1);
            }}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
          />
          </div>
        )}
        </div>
      </div>

      {/* modals */}

      <AddProcessor
        isOpen={addModal}
        onClose={() => { 
          setAddModal(false) 
          setActiveBank(null);
          setIsEdit(false);
        }}
        isEdit={isEdit}
        fetchData={fetchData}
      />

      {selectedBanks?.length > 0 && (
        <BulkAction
          fetchData={fetchData}
        />
      )}

      {/* Toaster global component */}
      <Toaster
        position="top-right"
        containerStyle={{}}
        toastOptions={{
          style: {
            borderRadius: "8px",
            background: "rgba(40, 39, 45, 0.85)",
            color: "#fff",
            padding: "16px 24px",
          },
          duration: 5000,
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <>
                {icon}
                {message}
                {t.type !== "loading" && (
                  <button
                    className={styles.toast_dismissal_button}
                    onClick={() => toast.dismiss(t.id)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                    >
                      <path
                        d="M12.8535 12.1462C12.9 12.1927 12.9368 12.2478 12.962 12.3085C12.9871 12.3692 13.0001 12.4343 13.0001 12.5C13.0001 12.5657 12.9871 12.6307 12.962 12.6914C12.9368 12.7521 12.9 12.8073 12.8535 12.8537C12.8071 12.9002 12.7519 12.937 12.6912 12.9622C12.6305 12.9873 12.5655 13.0003 12.4998 13.0003C12.4341 13.0003 12.369 12.9873 12.3083 12.9622C12.2476 12.937 12.1925 12.9002 12.146 12.8537L7.99979 8.70685L3.85354 12.8537C3.75972 12.9475 3.63247 13.0003 3.49979 13.0003C3.36711 13.0003 3.23986 12.9475 3.14604 12.8537C3.05222 12.7599 2.99951 12.6327 2.99951 12.5C2.99951 12.3673 3.05222 12.24 3.14604 12.1462L7.29291 7.99997L3.14604 3.85372C3.05222 3.7599 2.99951 3.63265 2.99951 3.49997C2.99951 3.36729 3.05222 3.24004 3.14604 3.14622C3.23986 3.0524 3.36711 2.99969 3.49979 2.99969C3.63247 2.99969 3.75972 3.0524 3.85354 3.14622L7.99979 7.2931L12.146 3.14622C12.2399 3.0524 12.3671 2.99969 12.4998 2.99969C12.6325 2.99969 12.7597 3.0524 12.8535 3.14622C12.9474 3.24004 13.0001 3.36729 13.0001 3.49997C13.0001 3.63265 12.9474 3.7599 12.8535 3.85372L8.70666 7.99997L12.8535 12.1462Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                )}
              </>
            )}
          </ToastBar>
        )}
      </Toaster>

    </div>
  )
}

export default ProcessorPage